import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant, useMarketingTheme } from '../context/TenantContext';

/** Theme-aware marketing footer — colophon-style minimal per theme. */
export const Footer: React.FC = () => {
  const { tenant } = useTenant();
  const theme = useMarketingTheme();
  const tenantName = tenant?.name ?? 'Kula Learn';
  const year = new Date().getFullYear();

  /* ─── EDITORIAL ────────────────────────────────────────────── */
  if (theme === 'editorial') {
    return (
      <footer className="bg-[var(--ed-ink)] text-[var(--ed-paper)] py-10 mt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-6">
          <div className="font-serif text-[20px]">
            <em>{tenantName}</em>
          </div>
          <div className="flex items-center gap-6 font-mono text-[11px] uppercase tracking-[0.12em] opacity-80">
            <Link to="/courses" className="hover:opacity-100">Catalogue</Link>
            <Link to="/privacy" className="hover:opacity-100">Privacy</Link>
            <Link to="/terms" className="hover:opacity-100">Terms</Link>
            <span>© {year}</span>
          </div>
        </div>
      </footer>
    );
  }

  /* ─── WARM ─────────────────────────────────────────────────── */
  if (theme === 'warm') {
    return (
      <footer className="bg-[var(--warm-ink)] text-[var(--warm-paper-deep)] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-4 text-sm">
          <div className="flex items-center gap-2.5 font-warm-serif text-[20px] text-[var(--warm-paper)]">
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--warm-accent)] text-white -rotate-[6deg]"
              style={{ fontFamily: 'Caveat, cursive', fontSize: '18px', fontWeight: 600 }}
            >
              {tenantName.charAt(0).toLowerCase()}
            </span>
            {tenantName}
          </div>
          <div className="flex items-center gap-5">
            <Link to="/courses" className="hover:text-[var(--warm-paper)] transition-colors">Programmes</Link>
            <Link to="/privacy" className="hover:text-[var(--warm-paper)] transition-colors">Privacy</Link>
            <span style={{ fontFamily: 'Caveat, cursive', fontSize: '18px', color: 'var(--warm-accent-soft)' }}>
              Made with care · {year}
            </span>
          </div>
        </div>
      </footer>
    );
  }

  /* ─── UTILITY ──────────────────────────────────────────────── */
  return (
    <footer className="bg-[var(--util-ink)] text-[var(--util-ink-faint)] border-t border-white/[0.06] py-6 font-util-mono text-[11px] tracking-[0.04em]">
      <div className="max-w-[1320px] mx-auto px-6 flex flex-wrap justify-between items-center gap-4">
        <span>© {year} {tenantName} · A Kula Learn school</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-white transition-colors">privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">terms</Link>
          <span className="text-white/40">v.4.2.1 · status: operational</span>
        </div>
      </div>
    </footer>
  );
};
